import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import memberApi from '../../lib/memberApi';
import { useMemberAuth } from '../../context/MemberAuthContext';
import { AuthLayout, inputCls } from '../../components/member/AuthLayout';

export default function MemberRegister() {
  const { isAuthenticated } = useMemberAuth();
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '', password2: '', kvkk: false, website: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  if (isAuthenticated) return <Navigate to="/profilim" replace />;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password2) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    if (!form.kvkk) {
      toast.error('KVKK aydınlatma metnini onaylamanız gerekiyor');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await memberApi.post('/members/auth/register', {
        first_name: form.first_name, last_name: form.last_name, email: form.email,
        phone: form.phone, password: form.password, kvkk: form.kvkk, website: form.website,
      });
      setDone(res.data);
      toast.success('Kayıt başarılı');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthLayout title="Kaydınız Alındı" testid="member-register-success">
        <div className="text-center space-y-3">
          <CheckCircle2 className="w-12 h-12 text-[hsl(var(--gold))] mx-auto" />
          <p className="text-sm text-muted-foreground">{done.message}</p>
          {done.dev_verification_link && (
            <div className="text-xs bg-secondary rounded-lg p-3 break-all">
              <span className="font-semibold block mb-1">Geliştirici Modu — Doğrulama Bağlantısı:</span>
              <a href={done.dev_verification_link} className="text-[hsl(var(--gold))] hover:underline" data-testid="member-dev-verify-link">{done.dev_verification_link}</a>
            </div>
          )}
          <Link to="/uye/giris" className="inline-block mt-2 text-sm text-[hsl(var(--gold))] font-medium hover:underline">Giriş sayfasına git</Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Üye Ol" subtitle="Yeni bir hesap oluşturun" testid="member-register-page"
      footer={<>Zaten üye misiniz? <Link to="/uye/giris" className="text-[hsl(var(--gold))] font-medium hover:underline">Giriş Yap</Link></>}>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="member-register-form">
        {/* Honeypot - hidden from real users */}
        <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set('website', e.target.value)}
          style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }} aria-hidden="true" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1.5">Ad</label>
            <input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className={inputCls} required data-testid="member-register-firstname" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Soyad</label>
            <input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className={inputCls} required data-testid="member-register-lastname" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">E-posta</label>
          <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputCls} required data-testid="member-register-email" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">Telefon</label>
          <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} className={inputCls} placeholder="05XX XXX XX XX" data-testid="member-register-phone" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1.5">Şifre</label>
            <input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} className={inputCls} required minLength={6} data-testid="member-register-password" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Şifre Tekrar</label>
            <input type="password" value={form.password2} onChange={(e) => set('password2', e.target.value)} className={inputCls} required minLength={6} data-testid="member-register-password2" />
          </div>
        </div>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.kvkk} onChange={(e) => set('kvkk', e.target.checked)} className="mt-0.5 accent-[hsl(var(--gold))]" data-testid="member-register-kvkk" />
          <span className="text-muted-foreground">KVKK aydınlatma metnini okudum, kişisel verilerimin işlenmesini onaylıyorum.</span>
        </label>
        <button type="submit" disabled={isSubmitting}
          className="w-full px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] font-medium text-sm hover:bg-[hsl(var(--gold)/0.92)] active:scale-[0.98] transition-colors duration-200 disabled:opacity-50"
          data-testid="member-register-submit">
          {isSubmitting ? 'Kaydınız oluşturuluyor...' : 'Kayıt Ol'}
        </button>
      </form>
    </AuthLayout>
  );
}
